import React, { useState } from "react";
import ConfirmationModal from "components/shared/confirmation-modal";
import Input from "components/input";
import ExpandableList from "components/shared/expandable-list";
import { useAppTranslation } from "locale/useAppTranslation";

interface DeleteDriverModalProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (reason: string) => Promise<void>;
    driverName?: string;
    selectedDriverNames?: string[];
    isLoading?: boolean;
}

const DeleteDriverModal: React.FC<DeleteDriverModalProps> = ({
    open,
    onClose,
    onConfirm,
    driverName = "-",
    selectedDriverNames = [],
    isLoading = false,
}) => {
    const { t } = useAppTranslation("driver");
    const [reason, setReason] = useState("");

    const isBulk = selectedDriverNames.length > 0;

    const handleConfirm = async () => {
        if (!reason.trim()) {
            return;
        }
        await onConfirm(reason);
    };

    // Reset reason when modal closes
    React.useEffect(() => {
        if (!open) {
            setReason("");
        }
    }, [open]);

    return (
        <ConfirmationModal
            open={open}
            title={
                isBulk
                    ? t("bulk-delete-title") || "Delete Drivers"
                    : t("modal-delete-title") || "Delete Driver"
            }
            message={
                isBulk
                    ? t("bulk-delete-message", { count: selectedDriverNames.length }) || `Are you sure you want to delete ${selectedDriverNames.length} selected driver(s)?`
                    : t("modal-delete-message", { name: driverName }) || `Are you sure you want to delete ${driverName}?`
            }
            confirmLabel={t("btn-delete") || "Delete"}
            cancelLabel={t("btn-cancel") || "Cancel"}
            onConfirm={handleConfirm}
            onCancel={onClose}
            icon="danger"
            loading={isLoading}
            processingLabel={t("btn-processing") || "Deleting..."}
        >
            <div className="mt-4 flex w-full flex-col gap-4 text-left">
                {isBulk && <ExpandableList items={selectedDriverNames} />}
                <Input
                    label={t("label-reason") || "Reason"}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder={t("placeholder-reason") || "Enter reason for deletion"}
                    disabled={isLoading}
                    required
                />
            </div>
        </ConfirmationModal>
    );
};

export default DeleteDriverModal;
